import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../utils/api";

export default function TrainerRegister() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [trainerName, setTrainerName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { navigate("/trainer/login"); return; }
    axios.get(`${API}/trainer/auth/verify-invite/${token}`)
      .then(res => {
        setTrainerName(res.data.name);
        setPhone(res.data.phone);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || "Invalid invite link");
        navigate("/trainer/login");
      })
      .finally(() => setVerifying(false));
  }, [token, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/trainer/auth/register`, { token, password });
      localStorage.setItem("trainer_token", res.data.token);
      localStorage.setItem("trainer_id", res.data.trainer_id);
      localStorage.setItem("trainer_name", res.data.name);
      toast.success("Account created! Welcome to NBC.");
      navigate("/trainer/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white/40">Verifying invite…</p>
      </div>
    );
  }

  const features = [
    {
      icon: "📊",
      badge: "LIVE STATS",
      title: "Your Dashboard",
      desc: "See your numbers in real time. Total classes taught, total check-ins, average attendance per class, and your best single class — all tracked automatically every time a member checks in.",
    },
    {
      icon: "💰",
      badge: "$20 PER CLASS · $0.30 PER CHECK-IN",
      title: "Auto-Tracked Payroll",
      desc: "Your pay is logged automatically — you never have to report it. Every check-in triggers your payroll. View your current week and week-by-week history anytime.",
    },
    {
      icon: "🔗",
      badge: "EARN $10 PER SIGNUP",
      title: "Your Referral Link",
      desc: "Every trainer gets a unique referral link. Share it and earn $10 every time someone signs up through your link and becomes a full-price paying member.",
    },
    {
      icon: "⭐",
      badge: "EARN $5 PER CONVERSION",
      title: "First-Timer Bonus",
      desc: "When a first-timer comes through First Class Free or a Free Week Pass, attends your class, and later becomes a paying member — you earn a $5 bonus automatically.",
    },
    {
      icon: "💪",
      badge: "MEMBER · NON-MEMBER · REQUESTED",
      title: "Personal Sessions",
      desc: "Log all your personal training sessions directly in the app. Member sessions carry a $5 rental deduction. Non-member sessions carry a $2 deduction. When a member purchases a $30 session request and you complete it — you earn $20.",
    },
    {
      icon: "🏆",
      badge: "6 CATEGORIES · SUNDAY NIGHT SMS",
      title: "Weekly Leaderboard",
      desc: "Compete with the NBC training team every week across 6 categories: classes taught, total check-ins, best average, biggest single class, first-timer conversions, and referral signups. Winners are announced by text every Sunday night.",
    },
  ];

  const earnings = [
    { action: "Teach a class",                   amount: "+$20.00",  positive: true },
    { action: "Member checks in",                amount: "+$0.30",   positive: true },
    { action: "First-timer joins after your class", amount: "+$5.00",positive: true },
    { action: "Your referral becomes a member",  amount: "+$10.00",  positive: true },
    { action: "Complete a requested personal",   amount: "+$20.00",  positive: true },
    { action: "Member personal rental",          amount: "−$5.00",   positive: false },
    { action: "Non-member personal rental",      amount: "−$2.00",   positive: false },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">

      {/* ── HERO ── */}
      <div className="border-b border-white/10 px-4 py-12 text-center">
        <p className="text-[#FFB612] font-black text-sm uppercase tracking-[0.2em] mb-3">
          Neighborhood Boxing Club
        </p>
        <h1 className="text-4xl sm:text-5xl font-black leading-tight">
          Welcome to the NBC<br />
          <span className="text-[#FFB612]">Trainer Portal,</span>{" "}
          {trainerName.split(" ")[0]}.
        </h1>
        <p className="text-white/50 text-base mt-4 max-w-xl mx-auto leading-relaxed">
          You've been invited to join the Neighborhood Boxing Club Trainer Dashboard — your
          personal hub for tracking performance, managing sessions, and getting paid automatically.
        </p>
        <p className="text-white/30 text-sm mt-3">
          Create your password below and get instant access.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-14">

        {/* ── FEATURES ── */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-[0.2em] mb-6 text-center">
            What You Get as an NBC Trainer
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {features.map(f => (
              <div key={f.title}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5 border-l-2 border-l-[#FFB612]">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">{f.icon}</span>
                  <div>
                    <span className="text-[10px] font-black tracking-widest text-[#FFB612] bg-[#FFB612]/10 px-2 py-0.5 rounded uppercase">
                      {f.badge}
                    </span>
                    <h3 className="text-white font-black text-base mt-1.5">{f.title}</h3>
                  </div>
                </div>
                <p className="text-white/50 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── EARNINGS TABLE ── */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-[0.2em] mb-6 text-center">
            How Your Pay Adds Up
          </p>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
            {earnings.map((row, i) => (
              <div key={i}
                className={`flex items-center justify-between px-5 py-3.5 ${
                  i < earnings.length - 1 ? "border-b border-white/5" : ""
                }`}>
                <span className="text-white/70 text-sm">{row.action}</span>
                <span className={`font-black text-base ${row.positive ? "text-[#FFB612]" : "text-red-400"}`}>
                  {row.amount}
                </span>
              </div>
            ))}
          </div>
          <p className="text-white/30 text-xs text-center mt-3 leading-relaxed">
            Pay is tracked weekly Monday through Sunday and locked every Monday at midnight.<br />
            View your full breakdown anytime inside the app.
          </p>
        </div>

        {/* ── REGISTRATION FORM ── */}
        <div className="max-w-sm mx-auto">
          <div className="text-center mb-6">
            <div className="inline-block bg-[#FFB612]/10 border border-[#FFB612]/30 rounded px-4 py-2 mb-4">
              <p className="text-[#FFB612] font-black text-sm uppercase tracking-wider">
                Almost Done
              </p>
            </div>
            <h2 className="text-xl font-black">Create Your Password</h2>
            <p className="text-white/40 text-sm mt-1">One step and your dashboard is ready.</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest mb-1 block">Phone Number</label>
              <input
                type="tel"
                value={phone}
                disabled
                className="w-full bg-white/5 border border-white/10 text-white/50 px-4 py-3 rounded cursor-not-allowed"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest mb-1 block">Create Password</label>
              <input
                data-testid="trainer-register-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 rounded focus:outline-none focus:border-[#FFB612]"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest mb-1 block">Confirm Password</label>
              <input
                data-testid="trainer-register-confirm"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                required
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 rounded focus:outline-none focus:border-[#FFB612]"
              />
            </div>
            <button
              data-testid="trainer-register-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFB612] text-black font-black py-4 rounded hover:bg-[#E5A00D] transition-colors disabled:opacity-50 text-base"
            >
              {loading ? "Setting up…" : "Continue to Your Trainer Dashboard →"}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
